"use client";

/**
 * fund-expense-chart.tsx
 *
 * Horizontal 3-bar comparison: this fund's expense per ₹1L vs category
 * average vs regular plan. Pure HTML/CSS — no chart library. Shows a green
 * "you save" or red "you overpay" callout below.
 *
 * Used in: src/app/funds/[slug]/page.tsx (Cost Breakdown section)
 */

import { formatRupees } from "@/lib/utils/format";

export interface FundExpenseChartProps {
  expensePerLakh: number | null;
  categoryAvgExpensePerLakh: number | null;
  regularPlanExpensePerLakh: number | null;
}

interface Bar {
  label: string;
  value: number;
  color: string;
  textColor: string;
}

export function FundExpenseChart({
  expensePerLakh,
  categoryAvgExpensePerLakh,
  regularPlanExpensePerLakh,
}: FundExpenseChartProps) {
  if (expensePerLakh === null || expensePerLakh === undefined) {
    return (
      <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
        <p className="text-muted-foreground text-sm">
          Expense ratio data not available.
        </p>
      </div>
    );
  }

  const bars: Bar[] = [
    {
      label: "This fund",
      value: expensePerLakh,
      color: "#10B981",
      textColor: "text-white",
    },
  ];
  if (categoryAvgExpensePerLakh !== null && categoryAvgExpensePerLakh !== undefined) {
    bars.push({
      label: "Category avg",
      value: categoryAvgExpensePerLakh,
      color: "#F59E0B",
      textColor: "text-white",
    });
  }
  if (regularPlanExpensePerLakh !== null && regularPlanExpensePerLakh !== undefined) {
    bars.push({
      label: "Regular plan",
      value: regularPlanExpensePerLakh,
      color: "#EF4444",
      textColor: "text-white",
    });
  }

  const maxValue = Math.max(...bars.map((b) => b.value));

  // Saving / overpay vs category
  const savingVsCategory =
    categoryAvgExpensePerLakh !== null && categoryAvgExpensePerLakh !== undefined
      ? categoryAvgExpensePerLakh - expensePerLakh
      : null;

  return (
    <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
      <div className="space-y-3">
        {bars.map((bar) => {
          const widthPct = maxValue > 0 ? (bar.value / maxValue) * 100 : 0;
          return (
            <div key={bar.label} className="flex items-center gap-3">
              <div className="text-muted-foreground text-xs w-[90px] text-right shrink-0">
                {bar.label}
              </div>
              <div className="flex-1 relative h-8 bg-surface-light/5 rounded overflow-hidden">
                <div
                  className="h-full rounded flex items-center justify-end pr-3 transition-all duration-700"
                  style={{
                    width: `${Math.max(widthPct, 10)}%`,
                    backgroundColor: bar.color,
                  }}
                >
                  <span className={`text-xs font-bold font-tabular ${bar.textColor}`}>
                    {formatRupees(bar.value)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Saving / overpay callout */}
      {savingVsCategory !== null && (
        <div className="mt-4 text-center text-xs">
          {savingVsCategory > 0 ? (
            <span className="text-cgreen font-semibold">
              You save {formatRupees(savingVsCategory)}/year vs category average
            </span>
          ) : savingVsCategory < 0 ? (
            <span className="text-cred font-semibold">
              {formatRupees(-savingVsCategory)}/year more than category average
            </span>
          ) : (
            <span className="text-muted-foreground">
              Same cost as category average
            </span>
          )}
        </div>
      )}
    </div>
  );
}
